import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../AuthContext';
import { teamsAPI, usersAPI } from '../../api';
import { Card, Button, Input, Select, Modal, Badge, Spinner, EmptyState } from '../../components/ui';
import { Users2, Plus, UserPlus, Crown, User, Check, X } from 'lucide-react';

export const TeamsPage = () => {
  const { user } = useAuth();
  const [myTeam, setMyTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [form, setForm] = useState({ name: '', description: '', max_members: 5 });
  const [selectedUser, setSelectedUser] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [teamsRes, invitesRes] = await Promise.all([
        teamsAPI.list(),
        teamsAPI.getPendingInvites()
      ]);
      setTeams(teamsRes.data);
      setInvites(invitesRes.data);

      try {
        const myTeamRes = await teamsAPI.getMyTeam();
        setMyTeam(myTeamRes.data);
        const membersRes = await teamsAPI.getMembers(myTeamRes.data.id);
        setMembers(membersRes.data);
      } catch (e) {
        setMyTeam(null);
        setMembers([]);
      }

      const usersRes = await usersAPI.list();
      setAvailableUsers(usersRes.data.filter(u => u.role === 'client'));
    } catch (err) {
      console.error('Failed to load teams:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      await teamsAPI.create(form);
      setShowCreateModal(false);
      setForm({ name: '', description: '', max_members: 5 });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleInvite = async () => {
    if (!selectedUser || !myTeam) return;
    try {
      await teamsAPI.invite(myTeam.id, selectedUser);
      setShowInviteModal(false);
      setSelectedUser('');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send invite');
    }
  };

  const handleAcceptInvite = async (teamId) => {
    try {
      await teamsAPI.acceptInvite(teamId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to accept invite');
    }
  };

  const handleDeclineInvite = async (teamId) => {
    try {
      await teamsAPI.declineInvite(teamId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to decline invite');
    }
  };

  const handleLeaveTeam = async () => {
    if (!window.confirm('Are you sure you want to leave this team?')) return;
    try {
      await teamsAPI.leave(myTeam.id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to leave team');
    }
  };

  const isCaptain = myTeam?.captain_id === user?.id;
  const memberIds = members.map(m => m.user_id);
  const invitableUsers = availableUsers.filter(u => !memberIds.includes(u.id) && u.id !== user?.id);

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="teams-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Teams</h1>
          <p className="text-gray-500">Join or create teams for group challenges</p>
        </div>
        {!myTeam && (
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" /> Create Team
          </Button>
        )}
      </div>

      {/* Pending Invites */}
      {invites.length > 0 && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-3">Team Invites</h3>
          <div className="space-y-3">
            {invites.map(inv => (
              <div key={inv.team_id} className="bg-white p-4 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium">{inv.team_name}</p>
                  <p className="text-sm text-gray-500">Invited by {inv.captain_name}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleAcceptInvite(inv.team_id)}>
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => handleDeclineInvite(inv.team_id)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* My Team */}
      {myTeam && (
        <Card>
          <div className="p-4 border-b flex items-center justify-between">
            <div>
              <h3 className="font-semibold">{myTeam.name}</h3>
              <p className="text-sm text-gray-500">{myTeam.member_count} / {myTeam.max_members} members</p>
            </div>
            <div className="flex gap-2">
              {isCaptain && (
                <Button size="sm" onClick={() => setShowInviteModal(true)}>
                  <UserPlus className="w-4 h-4 mr-1" /> Invite
                </Button>
              )}
              {!isCaptain && (
                <Button size="sm" variant="secondary" onClick={handleLeaveTeam}>
                  Leave Team
                </Button>
              )}
            </div>
          </div>
          <div className="divide-y">
            {members.map(member => (
              <div key={member.id} className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  {member.is_captain ? <Crown className="w-5 h-5 text-amber-500" /> : <User className="w-5 h-5 text-primary-600" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium">{member.user_name}</p>
                  <p className="text-xs text-gray-500">{member.is_captain ? 'Captain' : 'Member'}</p>
                </div>
                <Badge variant={member.status === 'active' ? 'success' : 'warning'}>
                  {member.status}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Browse Teams */}
      {!myTeam && (
        <div>
          <h3 className="font-semibold mb-3">Browse Teams</h3>
          {teams.length === 0 ? (
            <Card>
              <EmptyState
                icon={Users2}
                title="No teams yet"
                description="Be the first to create a team!"
                action={<Button onClick={() => setShowCreateModal(true)}>Create Team</Button>}
              />
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {teams.map(team => (
                <Card key={team.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold">{team.name}</h4>
                      <p className="text-sm text-gray-500">{team.description}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        Captain: {team.captain_name} • {team.member_count}/{team.max_members} members
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-primary-600">{team.total_points}</p>
                      <p className="text-xs text-gray-500">points</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Team Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create Team">
        <form onSubmit={handleCreateTeam} className="space-y-4">
          <Input label="Team Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
          <Input label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          <Input label="Max Members" type="number" value={form.max_members} onChange={e => setForm({ ...form, max_members: parseInt(e.target.value) })} min={2} max={20} />
          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowCreateModal(false)} className="flex-1">Cancel</Button>
            <Button type="submit" className="flex-1">Create Team</Button>
          </div>
        </form>
      </Modal>

      {/* Invite Modal */}
      <Modal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} title="Invite Member">
        <div className="space-y-4">
          <Select
            label="Select User"
            value={selectedUser}
            onChange={e => setSelectedUser(e.target.value)}
            options={[
              { value: '', label: 'Choose a user...' },
              ...invitableUsers.map(u => ({ value: u.id, label: u.name }))
            ]}
          />
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setShowInviteModal(false)} className="flex-1">Cancel</Button>
            <Button onClick={handleInvite} disabled={!selectedUser} className="flex-1">Send Invite</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TeamsPage;
